import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../context/AuthContext';
import { apiServices } from '../../api/services';
import { useTranslation } from '../../i18n/provider';
import { seoConfig } from '../../utils/seoConfig';
import PageShell from '../layout/PageShell';
import styles from './FeedbackPage.module.css';

const FeedbackPage: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState(user?.email || '');
  const [category, setCategory] = useState<'bug' | 'feature' | 'general' | 'improvement'>('general');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });

  const maxChars = 2000;
  const remainingChars = maxChars - message.length;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!subject.trim()) {
      setSubmitStatus({
        type: 'error',
        message: t('feedback.validation.subjectRequired'),
      });
      return;
    }
    
    if (!message.trim()) {
      setSubmitStatus({
        type: 'error',
        message: t('feedback.validation.messageRequired'),
      });
      return;
    }

    if (message.trim().length < 10) {
      setSubmitStatus({
        type: 'error',
        message: t('feedback.validation.messageMinLength'),
      });
      return;
    }

    if (!email.trim()) {
      setSubmitStatus({
        type: 'error',
        message: t('feedback.validation.emailRequired'),
      });
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setSubmitStatus({
        type: 'error',
        message: t('feedback.validation.emailInvalid'),
      });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus({ type: null, message: '' });

    try {
      // Map category to backend's type field
      const typeMapping = {
        bug: 'bug_report',
        feature: 'feature_request',
        general: 'general_feedback',
        improvement: 'general_feedback',
      } as const;
      
      await apiServices.feedback.submitFeedback({
        userId: user?.id || 'unknown',
        subject: subject.trim(),
        message: message.trim(),
        email: email.trim(),
        category,
        type: typeMapping[category],
      });

      setSubmitStatus({
        type: 'success',
        message: t('feedback.status.submitSuccess'),
      });
      setSubject('');
      setMessage('');
      setEmail(user?.email || '');
      setCategory('general');
    } catch (error) {
      console.error('Feedback submission error:', error);
      setSubmitStatus({
        type: 'error',
        message: t('feedback.status.submitFailed'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{seoConfig.feedback.title}</title>
        <meta name="description" content={seoConfig.feedback.description} />
        {seoConfig.feedback.keywords && <meta name="keywords" content={seoConfig.feedback.keywords} />}
        <meta property="og:title" content={seoConfig.feedback.title} />
        <meta property="og:description" content={seoConfig.feedback.description} />
        <meta property="og:type" content="website" />
        <meta name="twitter:title" content={seoConfig.feedback.title} />
        <meta name="twitter:description" content={seoConfig.feedback.description} />
        <meta name="twitter:card" content="summary" />
      </Helmet>
      <PageShell variant="content">
        <h1 className="content-title">{t('feedback.title')}</h1>

        <div className={styles.introText}>
          <p>
            {t('feedback.intro')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className={styles.feedbackForm}>
          <div className={styles.formGroup}>
            <label htmlFor="category" className={styles.label}>
              {t('feedback.form.category')}
            </label>
            <select
              id="category"
              value={category}
              onChange={(e) => setCategory(e.target.value as 'bug' | 'feature' | 'general' | 'improvement')}
              className={styles.select}
              disabled={isSubmitting}
            >
              <option value="general">{t('feedback.categories.general')}</option>
              <option value="bug">{t('feedback.categories.bug')}</option>
              <option value="feature">{t('feedback.categories.feature')}</option>
              <option value="improvement">{t('feedback.categories.improvementSuggestion')}</option>
            </select>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="subject" className={styles.label}>
              {t('feedback.form.subject')} <span className={styles.required}>*</span>
            </label>
            <input
              type="text"
              id="subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={t('feedback.form.subjectPlaceholder')}
              className={styles.input}
              disabled={isSubmitting}
              data-testid="feedback-subject"
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="message" className={styles.label}>
              {t('feedback.form.yourFeedback')} <span className={styles.required}>*</span>
            </label>
            <textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={maxChars}
              placeholder={t('feedback.form.messagePlaceholder')}
              className={styles.textarea}
              rows={8}
              disabled={isSubmitting}
              data-testid="feedback-message"
            />
            <div className={styles.charCounter}>
              {t('feedback.form.charactersRemaining', { count: remainingChars })}
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="email" className={styles.label}>
              {t('feedback.form.contactEmail')} <span className={styles.required}>*</span>
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('feedback.form.emailPlaceholder')}
              className={styles.input}
              disabled={isSubmitting}
              required
            />
            <div className={styles.helpText}>
              {t('feedback.form.helpText')}
            </div>
          </div>

          {submitStatus.type && (
            <div className={`${styles.statusMessage} ${styles[submitStatus.type]}`}>
              {submitStatus.message}
            </div>
          )}

          <button
            type="submit"
            className={styles.submitButton}
            disabled={isSubmitting || !message.trim() || !email.trim() || message.trim().length < 10}
          >
            {isSubmitting ? t('feedback.form.submitting') : t('feedback.form.submit')}
          </button>
        </form>
      </PageShell>
    </>
  );
};

export default FeedbackPage;
